/**
 * DonationsBlock — Premium plan
 * Stripe Connect Express onboarding + donations log.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2, AlertCircle, Heart } from "lucide-react";

const fmtMoney = (pence, currency) => {
  const sym = (currency || "gbp").toLowerCase() === "gbp" ? "£" : (currency || "").toUpperCase() + " ";
  return `${sym}${((pence || 0) / 100).toFixed(2)}`;
};

export default function DonationsBlock({ church }) {
  const [status, setStatus] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [st, ds] = await Promise.all([
        api.get(`/church-builder/churches/${church.id}/connect/status`),
        api.get(`/church-builder/churches/${church.id}/donations`),
      ]);
      setStatus(st.data);
      setDonations(ds.data || []);
    } catch (e) { toast.error("Could not load donations"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (church?.id) load(); /* eslint-disable-next-line */ }, [church?.id]);

  const onboard = async () => {
    setConnecting(true);
    try {
      const ret = `${window.location.origin}/church-builder/${church.slug}/dashboard?tab=donations&connect=done`;
      const ref = `${window.location.origin}/church-builder/${church.slug}/dashboard?tab=donations&connect=refresh`;
      const { data } = await api.post(`/church-builder/churches/${church.id}/connect/onboard`, { return_url: ret, refresh_url: ref });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Onboarding failed"); setConnecting(false); }
  };

  if (loading) return <div className="luxury-card rounded-xl p-6"><Loader2 className="animate-spin text-[#D4AF37]" size={16} /></div>;

  const ready = status?.charges_enabled;
  const total = donations.filter((d) => d.status === "paid").reduce((s, d) => s + (d.amount_total || d.amount_pence || 0), 0);

  return (
    <div className="space-y-6">
      <div data-testid="cb-donations-block" className="luxury-card rounded-xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-serif-display text-2xl flex items-center gap-3">
              Donations
              {ready ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] tracking-widest uppercase font-bold border border-emerald-500/40 bg-emerald-900/20 text-emerald-300"><CheckCircle2 size={11} /> Active</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] tracking-widest uppercase font-bold border border-amber-500/40 bg-amber-900/20 text-amber-300"><AlertCircle size={11} /> Setup required</span>
              )}
            </div>
            <div className="text-xs text-neutral-400 mt-1">Receive gifts directly into your own Stripe account (Connect Express).</div>
          </div>
          {!ready && (
            <button data-testid="cb-connect-onboard" disabled={connecting} onClick={onboard} className="btn-gold px-5 py-2 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2">
              {connecting ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />} {status?.configured ? "Continue Stripe onboarding" : "Connect Stripe"}
            </button>
          )}
        </div>

        {ready && (
          <div className="mt-5 grid grid-cols-3 gap-3">
            <Stat label="Connected" value={(status?.account_id || "").slice(0, 14) + "…"} />
            <Stat label="Total received" value={fmtMoney(total, donations[0]?.currency || "gbp")} />
            <Stat label="Donations" value={donations.length} />
          </div>
        )}
        {!ready && status?.configured && (
          <div className="mt-4 text-[11px] text-amber-300 inline-flex items-center gap-2"><AlertCircle size={12} /> Onboarding started but not complete. Click &quot;Continue Stripe onboarding&quot; to finish.</div>
        )}
      </div>

      <div className="luxury-card rounded-xl p-6" data-testid="cb-donations-log">
        <div className="font-serif-display text-xl flex items-center gap-2"><Heart size={16} className="text-[#D4AF37]" /> Recent gifts</div>
        <div className="mt-4 overflow-x-auto">
          {donations.length === 0 ? <div className="text-xs text-neutral-500">No donations yet.</div> :
            <table className="w-full text-xs">
              <thead><tr className="border-b border-white/10 text-[10px] tracking-widest uppercase text-neutral-500">
                <th className="text-left py-2">Donor</th>
                <th>Email</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr></thead>
              <tbody>
                {donations.map((d) => (
                  <tr key={d.id} className="border-b border-white/5">
                    <td className="py-2 text-white">{d.donor_name || "Anonymous"}</td>
                    <td className="text-neutral-400">{d.donor_email || "—"}</td>
                    <td className="text-[#D4AF37]">{fmtMoney(d.amount_total || d.amount_pence, d.currency)}</td>
                    <td className={d.status === "paid" ? "text-emerald-300" : "text-neutral-400"}>{d.status}</td>
                    <td className="text-neutral-500">{new Date(d.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
      <div className="text-[10px] tracking-widest uppercase text-neutral-500">{label}</div>
      <div className="font-serif-display text-xl text-[#D4AF37] mt-1 font-mono truncate" title={String(value)}>{value}</div>
    </div>
  );
}
